import { useEffect, useState } from "react";
import { api, fileUrl } from "./api";
import { flagUrl } from "./shield";
import { regionBadge } from "./regions";

const cache = new Map();

export const blobToDataUrl = (blob) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });

const downscale = (dataUrl, max) => new Promise((res) => {
  const img = new Image();
  img.onload = () => {
    const k = max / Math.max(img.naturalWidth, img.naturalHeight);
    if (k >= 1) return res(dataUrl);
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL("image/png"));
  };
  img.onerror = () => res(dataUrl);
  img.src = dataUrl;
});

export const fetchDataUrl = (url, max) => {
  const key = max ? `${url}@${max}` : url;
  if (!cache.has(key)) {
    const p = (max ? fetchDataUrl(url).then((d) => downscale(d, max)) : fetch(url)
      .then((r) => { if (!r.ok) throw new Error("Image failed to load"); return r.blob(); })
      .then(blobToDataUrl))
      .catch((e) => { cache.delete(key); throw e; });
    cache.set(key, p);
  }
  return cache.get(key);
};

export function useDataUrl(url, max) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null);
    if (url) fetchDataUrl(url, max).then((d) => live && setData(d)).catch(() => {});
    return () => { live = false; };
  }, [url, max]);
  return data;
}

export const useFileData = (file, max) => useDataUrl(file?.id ? fileUrl(file.id) : null, max);

// Preloads every image a design uses so off-screen renders are complete before export.
export const preloadDesigns = async (designs) => {
  const urls = designs.flatMap((d) => [d.photo.file, d.background.upload?.file, d.crest.file].filter(Boolean).map((f) => fileUrl(f.id))
    .concat(d.flag.code && !regionBadge(d.sport, d.flag) ? [flagUrl(d.flag.code)] : []));
  await Promise.all(urls.map((u) => fetchDataUrl(u).catch(() => null)));
  await new Promise((r) => setTimeout(r, 400));
};

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIDE = 2400;

// Shrinks big phone photos before upload (upright, ≤2400 px) so mobile uploads and previews stay fast.
async function shrink(file) {
  if (file.size < 1_500_000 || !window.createImageBitmap) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close?.();
    const png = file.type === "image/png";
    const blob = await new Promise((r) => c.toBlob(r, png ? "image/webp" : "image/jpeg", 0.9));
    if (!blob || (png && blob.type !== "image/webp")) return file;
    return new File([blob], file.name.replace(/\.\w+$/, "") + (png ? ".webp" : ".jpg"), { type: blob.type });
  } catch {
    return file;
  }
}

// Uploads to object storage; primes cache so the preview updates instantly.
export async function uploadImage(file) {
  if (!TYPES.includes(file.type)) throw new Error("Please choose a JPG, PNG or WebP image.");
  if (file.size > 15 * 1024 * 1024) throw new Error("Image is too large – please use a file under 15 MB.");
  const small = await shrink(file);
  const local = await blobToDataUrl(small);
  const form = new FormData();
  form.append("file", small);
  const { data } = await api.post("/uploads", form);
  cache.set(fileUrl(data.id), Promise.resolve(local));
  return { id: data.id, w: data.w, h: data.h };
}
